import React, { useEffect, useRef, useState } from "react";
import { fetchOwnPreferences, updateOwnPreferences } from "../api/client";
import {
  THEMES,
  applyThemePreference,
  readThemePreference,
  saveThemePreference,
} from "../themePreference";

function ThemePreview({ theme }) {
  return (
    <span className={"hacams-theme-preview hacams-theme-preview-" + theme.id} aria-hidden="true">
      <span className="hacams-theme-preview-dial" />
      <span className="hacams-theme-preview-primary" />
      <span className="hacams-theme-preview-service" />
    </span>
  );
}

export default function ThemeSelector({ compact = false }) {
  const [theme, setTheme] = useState(readThemePreference);
  const [saveMessage, setSaveMessage] = useState("");
  const userSelectedTheme = useRef(false);
  const saveSequence = useRef(0);
  const saveQueue = useRef(Promise.resolve());
  const current = THEMES.find((item) => item.id === theme) || THEMES[0];

  useEffect(() => {
    let active = true;
    const localTheme = readThemePreference();
    fetchOwnPreferences().then((payload) => {
      if (!active || userSelectedTheme.current) return;
      const serverTheme = payload?.preferences?.appearance?.theme;
      if (THEMES.some((item) => item.id === serverTheme)) {
        saveThemePreference(serverTheme);
        setTheme(serverTheme);
      } else {
        updateOwnPreferences({ appearance: { theme: localTheme } }).catch(() => {});
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    applyThemePreference(theme);
    const onStorage = (event) => {
      if (event.key === "hacams.appearance.theme") {
        userSelectedTheme.current = true;
        setTheme(readThemePreference());
      }
    };
    const onThemeChange = (event) => {
      userSelectedTheme.current = true;
      setTheme(event.detail?.theme || readThemePreference());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("hacams-theme-change", onThemeChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("hacams-theme-change", onThemeChange);
    };
  }, [theme]);

  const selectTheme = (value, event) => {
    userSelectedTheme.current = true;
    const selected = saveThemePreference(value);
    setTheme(selected);
    applyThemePreference(selected);
    const sequence = ++saveSequence.current;
    setSaveMessage("Saving preference…");
    window.dispatchEvent(new CustomEvent("hacams-theme-change", { detail: { theme: selected } }));
    const menu = event.currentTarget.closest("details");
    if (menu) menu.open = false;
    const save = saveQueue.current.then(() => updateOwnPreferences({ appearance: { theme: selected } }));
    saveQueue.current = save.catch(() => {});
    save.then((result) => {
      if (result?.success === false) throw new Error("Profile preference was not saved.");
      if (sequence === saveSequence.current) setSaveMessage("Appearance saved to your profile.");
    }).catch(() => {
      if (sequence === saveSequence.current) setSaveMessage("Theme applied on this device, but profile save failed.");
    });
  };

  return (
    <div className="hacams-theme-control-wrap">
    <details className={"hacams-theme-control" + (compact ? " hacams-theme-control-compact" : "")}>
      <summary aria-label={"Appearance theme: " + current.name}>
        <span className="hacams-theme-control-label">Appearance</span>
        <ThemePreview theme={current} />
        <strong>{current.name}</strong>
      </summary>
      <div className="hacams-theme-menu" role="group" aria-label="Choose appearance theme">
        {THEMES.map((item) => (
          <button
            key={item.id}
            type="button"
            className="hacams-theme-option"
            aria-pressed={theme === item.id}
            onClick={(event) => selectTheme(item.id, event)}
          >
            <ThemePreview theme={item} />
            <span className="hacams-theme-option-copy">
              <strong>{item.name}</strong>
              <small>{item.description}</small>
            </span>
            <span className="hacams-theme-option-state">{theme === item.id ? "Selected" : "Select"}</span>
          </button>
        ))}
      </div>
    </details>
    {saveMessage && <span className="hacams-theme-save-status" role="status">{saveMessage}</span>}
    </div>
  );
}
